import React from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import NotificationBell from '../common/NotificationBell';
import { Toaster } from '../../components/ui/sonner';
import { useAuth } from '../../context/AuthContext';
import { Loader2, Menu, Building2 } from 'lucide-react';
import ForcePasswordChange from '../auth/ForcePasswordChange';
import { SidebarProvider, useSidebar } from './SidebarContext';
import { roleHomePath } from '../../lib/roles';

const MobileHeader = () => {
  const { toggle } = useSidebar();
  return (
    <div
      className="lg:hidden sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-3 sm:px-4 py-2.5 flex items-center justify-between gap-3"
      data-testid="mobile-header"
    >
      <button
        onClick={toggle}
        className="p-2 -ml-1 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
        aria-label="Open menu"
        data-testid="mobile-menu-toggle"
      >
        <Menu className="w-6 h-6 text-gray-700" />
      </button>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 bg-gradient-primary rounded-lg flex items-center justify-center">
          <Building2 className="w-4 h-4 text-white" />
        </div>
        <span className="text-base font-bold text-gradient-brand">Sam Rumah</span>
      </div>
      <NotificationBell />
    </div>
  );
};

const DesktopHeader = () => (
  <div className="hidden lg:flex sticky top-0 z-30 bg-white/80 backdrop-blur-sm border-b border-gray-100 px-6 py-3 justify-end items-center gap-4">
    <NotificationBell />
  </div>
);

const LayoutBody = ({ children }) => (
  <div className="min-h-screen bg-[#F8FAFC]">
    <Sidebar />
    <main className="lg:ml-56 min-h-screen">
      <MobileHeader />
      <DesktopHeader />
      <div className="p-3 sm:p-4 lg:p-6">
        {children || <Outlet />}
      </div>
    </main>
    <Toaster position="top-right" richColors />
  </div>
);

const DashboardLayout = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Not logged in → send to login, remember where they were going
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Force temp-password change before allowing access to any protected page
  if (user.force_password_change) {
    return <ForcePasswordChange />;
  }

  // Logged in but wrong role for this layout group → bounce to their home
  if (allowedRoles && allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to={roleHomePath(user.role)} replace />;
  }

  return (
    <SidebarProvider>
      <LayoutBody>{children}</LayoutBody>
    </SidebarProvider>
  );
};

export default DashboardLayout;
